// The navigation drawer (YouTube-shell rework, Aug 2026): the hamburger's
// slide-in panel at EVERY width — destinations on top, the application
// preferences beneath (see PreferencesPanel), one scrim over the page. It
// overlays rather than reflows, so opening it never shifts the score.

import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, Home, Menu, Plus, ShieldCheck } from 'lucide-react';
import { APP_ROUTES, PRIMARY_NAV } from '@/navigation';
import { PreferencesPanel } from '@/components/GlobalControls';
import { AccountSection } from '@/components/AccountSection';
import { FeedbackLink } from '@/components/FeedbackLink';
import { useBrandText } from '@/components/brand';
import { useAuth } from '@/auth/session';
import { usePlaylists, usePlaylistsAvailable } from '@/data/playlistAccess';

export function NavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  const brand = useBrandText();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Escape closes; focus moves into the panel on open so keyboard users are
  // where their click put them. Focus RETURN is the opener's job (App keeps
  // the hamburger mounted). Keyed on `open` ALONE, with the latest onClose read
  // through a ref: a caller passing an inline callback hands us a new one on
  // every render, and App re-renders on the very preferences this drawer sets
  // (theme, visual style, keep awake) — so an `onClose` dependency pulled focus
  // back to the close button, scrolling the panel to the top, after every tap.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('a, button, input, select')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={`nav-drawer${open ? ' is-open' : ''}`} aria-hidden={!open}>
      <div className="nav-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        id="nav-drawer-panel"
        className="nav-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation and preferences"
      >
        {/* The header's hamburger + logo repeat here, in the same spot on
            screen (YouTube's trick): the toggle visibly stays put, and the
            drawer carries its own way to close. */}
        <div className="drawer-head">
          <button
            type="button"
            className="nav-drawer-trigger"
            aria-label="Close navigation"
            aria-controls="nav-drawer-panel"
            aria-expanded={open}
            onClick={onClose}
          >
            <Menu size={19} strokeWidth={2} aria-hidden="true" />
          </button>
          <NavLink
            to={APP_ROUTES.home}
            className="brand"
            aria-label={brand.homeLabel}
            onClick={onClose}
          >
            <span className="mark brand-wordmark" aria-hidden="true">
              {brand.wordmark}
            </span>
            <span className="mark brand-logo" aria-hidden="true" />
            {brand.sub ? <span className="sub">{brand.sub}</span> : null}
          </NavLink>
        </div>
        <nav className="drawer-nav" aria-label="Primary">
          <NavLink to={APP_ROUTES.home} end onClick={onClose}>
            <Home size={16} aria-hidden="true" /> Home
          </NavLink>
          {PRIMARY_NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={onClose}>
              {item.label}
            </NavLink>
          ))}
          <AdminLink onClose={onClose} />
          <FeedbackLink className="feedback-nav" onOpen={onClose} />
        </nav>
        <hr className="drawer-divider" />
        <PlaylistsSection onClose={onClose} />
        <hr className="drawer-divider" />
        <PreferencesPanel />
        <AccountFoot />
        <NavLink to={APP_ROUTES.privacy} className="drawer-privacy-link" onClick={onClose}>
          Privacy
        </NavLink>
      </div>
    </div>
  );
}

/** Account LAST (user ruling Sep 2026): the drawer opens on the destinations
 * and preferences you reach for daily; who is signed in is checked
 * occasionally, so it sits at the foot. The divider travels WITH the section —
 * an unconfigured build renders neither, or the drawer would end on a stray
 * rule (AccountSection itself renders nothing there). */
function AccountFoot() {
  const auth = useAuth();
  if (auth.status === 'unconfigured') return null;
  return (
    <>
      <hr className="drawer-divider" />
      <AccountSection />
    </>
  );
}

/** Shown to admins only — cosmetic gating; the page and RLS refuse everyone
 * else. Not a PRIMARY_NAV row because that list is static shared config. */
function AdminLink({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  if (auth.role !== 'admin') return null;
  return (
    <NavLink to={APP_ROUTES.admin} end onClick={onClose} data-testid="nav-admin">
      <ShieldCheck size={16} aria-hidden="true" /> Admin
    </NavLink>
  );
}

/**
 * The player's own sets, in the drawer (Aug 2026).
 *
 * Named destinations under a section header rather than one "Playlists" link,
 * because a set list is something a player arrives with: the useful question
 * on opening the drawer is "take me to Friday Gig", not "show me the index and
 * let me find it". Playlists were reachable from the ⌘K palette and the home
 * shelf but had no place in the nav at all.
 *
 * The header is BOTH a link and a toggle, deliberately kept as two targets:
 * the word navigates to the index, the chevron only expands and collapses.
 * One control doing both would make either a click that navigates when you
 * meant to collapse, or a header that cannot be reached at all.
 */
function PlaylistsSection({ onClose }: { onClose: () => void }) {
  const userPlaylists = usePlaylists();
  // Signed out there are no playlists to list or make: sign-in is in the
  // Account section below, and on the Playlists page.
  const available = usePlaylistsAvailable();
  const navigate = useNavigate();
  // Open by default — a collapsed section is a section nobody discovers. The
  // drawer stays mounted (it slides, it does not unmount), so this survives
  // closing and reopening within a session, which is the span that matters.
  const [expanded, setExpanded] = useState(true);

  return (
    <section className="drawer-section" aria-labelledby="drawer-playlists-heading">
      <div className="drawer-section-head">
        <NavLink
          id="drawer-playlists-heading"
          className="drawer-section-title"
          to={APP_ROUTES.playlists}
          end
          onClick={onClose}
        >
          Playlists
        </NavLink>
        <button
          type="button"
          className="drawer-section-toggle"
          aria-expanded={expanded}
          aria-controls="drawer-playlists-list"
          aria-label={expanded ? 'Collapse playlists' : 'Expand playlists'}
          onClick={() => setExpanded((v) => !v)}
        >
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </div>

      {expanded && (
        <div className="drawer-section-body" id="drawer-playlists-list">
          {!available ? (
            <p className="drawer-section-empty muted" data-testid="drawer-playlists-signin">
              Sign in to see your playlists.
            </p>
          ) : userPlaylists.length === 0 ? (
            <p className="drawer-section-empty muted">No playlists yet.</p>
          ) : (
            <nav aria-label="Playlists">
              {userPlaylists.map((p) => (
                <NavLink
                  key={p.id}
                  className="drawer-playlist"
                  to={`${APP_ROUTES.playlists}/${p.id}`}
                  onClick={onClose}
                >
                  <span className="drawer-playlist-name">{p.name}</span>
                  <span className="drawer-playlist-count muted" aria-hidden="true">
                    {p.entries.length}
                  </span>
                  {/* The count is decoration beside the name, but a screen
                      reader needs it said properly or not at all. */}
                  <span className="visually-hidden">
                    {p.entries.length} {p.entries.length === 1 ? 'tune' : 'tunes'}
                  </span>
                </NavLink>
              ))}
            </nav>
          )}
          {available && (
            <button
              type="button"
              className="drawer-playlist-new"
              data-testid="drawer-playlist-new"
              onClick={() => {
                onClose();
                // Hands off to the index's own create form rather than putting a
                // second naming input in the drawer — one place that makes a
                // playlist, and you land where the new one will appear.
                navigate(APP_ROUTES.playlists, { state: { create: true } });
              }}
            >
              <Plus size={15} aria-hidden="true" /> New playlist
            </button>
          )}
        </div>
      )}
    </section>
  );
}
